/**
 * IndexedDB Storage for Token Pricing Data via Dexie
 *
 * Stores token pricing data with associated pool information for efficient
 * querying and offline access. Optimized for React 19.2 and modern web standards.
 */

import Dexie, { Table } from 'dexie'

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalize address by removing chain prefix and converting to lowercase
 * Example: "eth_0xABC..." -> "0xabc..."
 * Example: "solana_EPjF..." -> "epjf..."
 */
function normalizeAddress(address: string): string {
  if (!address) return ''

  // Remove chain prefix (anything before underscore)
  const withoutPrefix = address.includes('_')
    ? address.split('_')[1]
    : address

  return withoutPrefix.toLowerCase()
}

// ============================================================================
// Types
// ============================================================================

/**
 * Price change percentages for different timeframes
 */
export interface PriceChangePercentage {
  m5: string
  m15: string
  m30: string
  h1: string
  h6: string
  h24: string
}

/**
 * Transaction statistics for different timeframes
 */
export interface TransactionStats {
  buys: number
  sells: number
  buyers: number
  sellers: number
}

/**
 * Transactions across different timeframes
 */
export interface Transactions {
  m5: TransactionStats
  m15: TransactionStats
  m30: TransactionStats
  h1: TransactionStats
  h6: TransactionStats
  h24: TransactionStats
}

/**
 * Volume data across different timeframes
 */
export interface VolumeUsd {
  m5: string
  m15: string
  m30: string
  h1: string
  h6: string
  h24: string
}

/**
 * Pool information with full stats
 */
export interface TokenPool {
  // Identity
  id: string // Pool address
  address: string
  name: string // e.g., "POPFROG / SOL"
  poolCreatedAt: string // ISO timestamp

  // Liquidity
  reserveInUsd: string

  // Base Token Pricing
  baseTokenPriceUsd: string
  baseTokenPriceNative: string
  baseTokenBalance: string

  // Quote Token Pricing
  quoteTokenPriceUsd: string
  quoteTokenPriceNative: string
  quoteTokenBalance: string

  // Price relationships
  baseTokenPriceQuoteToken: string
  quoteTokenPriceBaseToken: string

  // Market stats
  fdvUsd: string
  marketCapUsd: string

  // Price changes
  priceChangePercentage: PriceChangePercentage

  // Transaction stats
  transactions: Transactions

  // Volume
  volumeUsd: VolumeUsd

  // Relationships
  baseTokenAddress: string
  quoteTokenAddress: string
  dexId: string
}

/**
 * Enhanced token price data with pools
 */
export interface CachedTokenPrice {
  // Primary key: chainId:tokenAddress
  id: string

  // Identity
  chainId: number | string
  address: string
  symbol: string
  name: string
  decimals: number

  // Visual
  imageUrl: string | null

  // Pricing
  priceUsd: string
  fdvUsd: string | null
  marketCapUsd: string | null
  totalReserveInUsd: string | null

  // Volume
  volumeUsd24h: string | null

  // Supply
  totalSupply: string | null
  normalizedTotalSupply: string | null
  circulatingSupply: string | null

  // CoinGecko integration
  coingeckoCoinId: string | null

  // Associated pools (sorted by liquidity, highest first)
  pools: TokenPool[]

  // Metadata
  timestamp: number // When cached
  expiresAt: number // TTL expiration
}

/**
 * Cache statistics
 */
export interface CacheStats {
  totalTokens: number
  totalPools: number
  cacheSize: number // Approximate size in bytes
  oldestEntry: number
  newestEntry: number
}

// ============================================================================
// Dexie Database
// ============================================================================

export class PricingDatabase extends Dexie {
  // Tables
  tokens!: Table<CachedTokenPrice, string>

  constructor() {
    super('TokenPricingDB')

    this.version(1).stores({
      // Tokens table
      // Primary index: id (chainId:address)
      // Secondary indices: chainId, address, timestamp, expiresAt
      tokens: 'id, chainId, address, timestamp, expiresAt',
    })
  }

  /**
   * Store token price with pools
   */
  async cacheTokenPrice(
    chainId: number | string,
    tokenData: any,
    pools: TokenPool[],
    ttlMs: number = 60000
  ): Promise<void> {
    // Normalize address (remove chain prefix like "eth_" or "solana_")
    const normalizedAddress = normalizeAddress(tokenData.address || '')
    const id = `${chainId}:${normalizedAddress}`
    const now = Date.now()

    const cachedToken: CachedTokenPrice = {
      id,
      chainId,
      address: normalizedAddress,
      symbol: tokenData.symbol || '',
      name: tokenData.name || '',
      decimals: tokenData.decimals || 0,
      imageUrl: tokenData.image_url || null,
      priceUsd: tokenData.price_usd || '0',
      fdvUsd: tokenData.fdv_usd || null,
      marketCapUsd: tokenData.market_cap_usd || null,
      totalReserveInUsd: tokenData.total_reserve_in_usd || null,
      volumeUsd24h: tokenData.volume_usd?.h24 || null,
      totalSupply: tokenData.total_supply || null,
      normalizedTotalSupply: tokenData.normalized_total_supply || null,
      circulatingSupply: tokenData.circulating_supply || null,
      coingeckoCoinId: tokenData.coingecko_coin_id || null,
      pools: pools.sort(
        (a, b) =>
          parseFloat(b.reserveInUsd || '0') -
          parseFloat(a.reserveInUsd || '0')
      ),
      timestamp: now,
      expiresAt: now + ttlMs,
    }

    await this.tokens.put(cachedToken)
  }

  /**
   * Get cached token price if not expired
   */
  async getCachedTokenPrice(
    chainId: number | string,
    address: string
  ): Promise<CachedTokenPrice | null> {
    // Normalize address for lookup
    const normalizedAddress = normalizeAddress(address)
    const id = `${chainId}:${normalizedAddress}`
    const cached = await this.tokens.get(id)

    if (!cached) return null

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      await this.tokens.delete(id)
      return null
    }

    return cached
  }

  /**
   * Get multiple cached token prices
   */
  async getCachedTokenPrices(
    chainId: number | string,
    addresses: string[]
  ): Promise<Map<string, CachedTokenPrice>> {
    const result = new Map<string, CachedTokenPrice>()
    const now = Date.now()

    for (const address of addresses) {
      // Normalize address for lookup
      const normalizedAddress = normalizeAddress(address)
      const id = `${chainId}:${normalizedAddress}`
      const cached = await this.tokens.get(id)

      if (cached && now <= cached.expiresAt) {
        // Use normalized address as key for consistency
        result.set(normalizedAddress, cached)
      } else if (cached) {
        // Clean up expired entry
        await this.tokens.delete(id)
      }
    }

    return result
  }

  /**
   * Clear expired entries
   */
  async clearExpired(): Promise<number> {
    const now = Date.now()
    const expired = await this.tokens.where('expiresAt').below(now).toArray()
    const count = expired.length

    if (count > 0) {
      await this.tokens.bulkDelete(expired.map((t: CachedTokenPrice) => t.id))
    }

    return count
  }

  /**
   * Clear all cached data for a chain
   */
  async clearChain(chainId: number | string): Promise<number> {
    const tokens = await this.tokens.where('chainId').equals(chainId).toArray()
    const count = tokens.length

    if (count > 0) {
      await this.tokens.bulkDelete(tokens.map((t: CachedTokenPrice) => t.id))
    }

    return count
  }

  /**
   * Clear all cached data
   */
  async clearAll(): Promise<void> {
    await this.tokens.clear()
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<CacheStats> {
    const allTokens = await this.tokens.toArray()

    if (allTokens.length === 0) {
      return {
        totalTokens: 0,
        totalPools: 0,
        cacheSize: 0,
        oldestEntry: 0,
        newestEntry: 0,
      }
    }

    const totalPools = allTokens.reduce((sum: number, token: CachedTokenPrice) => sum + token.pools.length, 0)

    // Approximate size calculation
    const cacheSize = JSON.stringify(allTokens).length

    const timestamps = allTokens.map((t: CachedTokenPrice) => t.timestamp)
    const oldestEntry = Math.min(...timestamps)
    const newestEntry = Math.max(...timestamps)

    return {
      totalTokens: allTokens.length,
      totalPools,
      cacheSize,
      oldestEntry,
      newestEntry,
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let dbInstance: PricingDatabase | null = null

/**
 * Get or create database instance
 */
export function getPricingDB(): PricingDatabase {
  if (!dbInstance) {
    dbInstance = new PricingDatabase()
  }
  return dbInstance
}

/**
 * Reset database instance (useful for testing)
 */
export async function resetPricingDB(): Promise<void> {
  if (dbInstance) {
    await dbInstance.delete()
    dbInstance = null
  }
}
